import { css } from '@emotion/react'
import { Button, Cell, FormControl, Grid, Heading, Icon, Text, VFlow } from 'bold-ui'
import { useAcessoLotacaoOrEstagio } from 'components/auth/useAcessoLotacao'
import { FormRenderProps } from 'components/form'
import { DateRangeField } from 'components/form/field/daterange/DateRangeField'
import { EquipeResponsavelField } from 'components/form/field/select/EquipeResponsavelSelectField/EquipeResponsavelField'
import { MicroareaSelectField } from 'components/form/field/select/MicroareaSelectField/MicroareaSelectField'
import { RacaCorSelectField } from 'components/form/field/select/RacaCorSelectField'
import { useFlags } from 'config/useFlagsContext'
import { AcompanhamentoCondicaoSaudeTematicaEnum } from 'graphql/types.generated'
import { useAnalytics } from 'hooks/analytics/useAnalytics'
import { useServerTime } from 'hooks/useServerTime'
import { Fragment, useMemo } from 'react'
import { Route, Switch, useHistory, useRouteMatch } from 'react-router'
import Permissions from 'types/Permissions.generated'
import { isUndefinedOrNull } from 'util/checks'
import { metaPath } from 'util/metaPath'
import { matchesWithPartial } from 'util/object'
import { isEmpty } from 'util/validation/Util'

import { FaixaEtariaFilterGroup } from '../components/FaixaEtariaFilterGroup'
import { SexoIdentidadeGeneroButtonGroup } from '../components/SexoIdentidadeGeneroButtonGroup'
import {
  AcompanhamentoCondicaoSaudeFiltrosAvancadosModal,
  FILTROS_AVANCADOS_URL,
} from './components/filter/AcompanhamentoCondicaoSaudeFiltrosAvancadosModal'
import { AcompanhamentoCondicaoSaudeProblemasCondicoesFilter } from './components/filter/AcompanhamentoCondicaoSaudeProblemasCondicoesFilter'
import { VulnerabilidadeFilterGroup } from './components/filter/VulnerabilidadeFilterGroup'
import { ListaTematicaSection } from './components/lista-tematica/ListaTematicaSection'
import {
  AcompanhamentoCondicaoSaudeFiltrosAvancadosModel,
  AcompanhamentoCondicaoSaudeFormModel,
  defaultFilterAcompanhamentoCondicaoSaudeByTematica,
} from './model/model-acompanhamentoCondicaoSaude'
import { SearchFooter } from './SearchFooter'
import { contarFiltrosAvancadosAplicados } from './utils-acompanhamentoCondicaoSaude'
import { getAcompanhamentoCondicaoSaudeSubmitAnalyticsEvents } from './utils-acompanhamentoCondicaoSaudeAnalytics'

const path = metaPath<AcompanhamentoCondicaoSaudeFormModel>()

interface AcompanhamentoCondicaoSaudeFormProps extends FormRenderProps<AcompanhamentoCondicaoSaudeFormModel> {
  setListaTematica: (listaTematica: AcompanhamentoCondicaoSaudeTematicaEnum) => void
  loading: boolean
  abortQuery: () => void
}

export function AcompanhamentoCondicaoSaudeForm(props: Readonly<AcompanhamentoCondicaoSaudeFormProps>) {
  const { values = {}, form, initialValues, handleSubmit, setListaTematica, loading, abortQuery } = props

  const { acesso, hasAuthorization } = useAcessoLotacaoOrEstagio()
  const { FILTROS_ACOMPANHAMENTO_CONDICOES_SAUDE_ENABLED } = useFlags()
  const { analytics } = useAnalytics()
  const { getServerTimeNow } = useServerTime()
  const now = getServerTimeNow()

  const history = useHistory()
  const match = useRouteMatch()

  const isCoordenador = hasAuthorization(Permissions.acompanhamentos.condicoesDeSaude.todasAsEquipes)
  const isAbleToBuscar = isCoordenador || !isUndefinedOrNull(values.equipeResponsavel?.id)

  const isTematicaGeral = values.tematica === AcompanhamentoCondicaoSaudeTematicaEnum.GERAL
  const hasFiltrosAvancados = !isTematicaGeral || FILTROS_ACOMPANHAMENTO_CONDICOES_SAUDE_ENABLED

  const canCheckApenasProblemasCondicoesAtivas =
    isEmpty(values.filtrosAvancados?.grupoCondicao) && isEmpty(values.filtrosAvancados?.problemasCondicoes)

  const handleOnSubmit = () => {
    const values = form.getState().values
    getAcompanhamentoCondicaoSaudeSubmitAnalyticsEvents(values).forEach((event) => analytics.logEvent(event))
    setListaTematica(values?.tematica)
    handleSubmit()
  }

  const handleSubmitFiltrosAvancadosModalForm = (
    filtrosAvancados: AcompanhamentoCondicaoSaudeFiltrosAvancadosModel
  ) => {
    form.change(path.filtrosAvancados.absolutePath(), filtrosAvancados)
    handleOnSubmit()
  }

  const handleChangeListaTematica = (listaSelecionada: AcompanhamentoCondicaoSaudeTematicaEnum) => {
    if (loading) {
      abortQuery()
    }

    if (listaSelecionada !== AcompanhamentoCondicaoSaudeTematicaEnum.GERAL) {
      isAbleToBuscar && handleOnSubmit()
    }
  }

  const handleResetFilterToTematicaDefault = () => {
    const defaultFilter = defaultFilterAcompanhamentoCondicaoSaudeByTematica[values.tematica]
    form.change('', { ...values, ...(defaultFilter ?? initialValues) })
  }

  const handleFiltrosAvancadosClick = () => {
    history.push(`${match.url}${FILTROS_AVANCADOS_URL}`)
  }

  const countFiltrosAplicados = useMemo(() => {
    return contarFiltrosAvancadosAplicados(values)
  }, [values])

  const isFilterSetToDefault = useMemo(() => {
    return matchesWithPartial(values, defaultFilterAcompanhamentoCondicaoSaudeByTematica[values.tematica])
  }, [values])

  return (
    <VFlow vSpacing={2}>
      <Grid wrap>
        <Cell size={3} style={styles.cell}>
          <FormControl label='Unidade responsável'>
            <Text>{acesso?.unidadeSaude?.nome.titleCase()}</Text>
          </FormControl>
        </Cell>
        <Cell size={isCoordenador ? 4 : 3} style={styles.cell}>
          <EquipeResponsavelField
            name={path.equipeResponsavel}
            equipe={values?.equipeResponsavel}
            unidadeSaudeId={values.unidadeResponsavelId}
            hasAccessToTodasEquipes={isCoordenador}
            placeholder='Selecione uma equipe'
            required={isCoordenador}
          />
        </Cell>
        <Cell size={3} style={styles.cell}>
          <MicroareaSelectField
            name={path.microareas}
            label='Microárea'
            metaEquipe={path.equipeResponsavel}
            placeholder='Todas as microáreas'
            disabled={isCoordenador && !values.equipeResponsavel}
          />
        </Cell>
        <Cell size={12}>
          <ListaTematicaSection name={path.tematica} handleChangeListaTematica={handleChangeListaTematica} />
        </Cell>
        <Cell size={12}>
          <Grid>
            {isTematicaGeral && (
              <Fragment>
                <AcompanhamentoCondicaoSaudeProblemasCondicoesFilter
                  permission={Permissions.acompanhamentos.condicoesDeSaude.problemasECondicoes}
                  path={path.filtrosAvancados}
                  canCheckApenasProblemasCondicoesAtivas={canCheckApenasProblemasCondicoesAtivas}
                />
                <Cell size={12}>
                  <Heading level={2}>Informações da pessoa</Heading>
                </Cell>
                <Cell size={6}>
                  <SexoIdentidadeGeneroButtonGroup<AcompanhamentoCondicaoSaudeFiltrosAvancadosModel>
                    onChange={form.change}
                    meta={path.filtrosAvancados}
                  />
                </Cell>
                <Cell size={6}>
                  <DateRangeField
                    name={path.filtrosAvancados.periodoUltimoAtendimento.absolutePath()}
                    label='Período do último atendimento'
                    // TODO eldorado #916 (bold): corrigir as props startPlaceholder e finalPlaceholder
                    // startPlaceholder='Data inicial'
                    // finalPlaceholder='Data final'
                    maxDate={now}
                  />
                </Cell>
                <Fragment>
                  <Cell size={12}>
                    <Heading level={4}>Habilitar filtros:</Heading>
                  </Cell>
                  <Cell size={12}>
                    <VulnerabilidadeFilterGroup meta={path} />
                  </Cell>
                </Fragment>
                <Cell size={5}>
                  <VFlow>
                    <Heading level={5}>Raça/Cor</Heading>
                    <RacaCorSelectField name={path.filtrosAvancados.racaCor} />
                  </VFlow>
                </Cell>
                <Cell size={12}>
                  <FaixaEtariaFilterGroup<AcompanhamentoCondicaoSaudeFiltrosAvancadosModel>
                    change={form.change}
                    meta={path.filtrosAvancados}
                    faixaEtariaFilter={values.filtrosAvancados?.tipoFaixaEtaria}
                    tipoVulnerabilidade={values.tipoVulnerabilidadeSelected}
                  />
                </Cell>
              </Fragment>
            )}
            {hasFiltrosAvancados && (
              <Cell size={12}>
                <Button kind='primary' size='small' skin='outline' onClick={handleFiltrosAvancadosClick}>
                  <Icon icon='filterOutline' />
                  Filtros avançados ({countFiltrosAplicados})
                </Button>
                <Switch>
                  <Route exact path={`${match.url}${FILTROS_AVANCADOS_URL}`}>
                    <AcompanhamentoCondicaoSaudeFiltrosAvancadosModal
                      now={now}
                      onSubmit={handleSubmitFiltrosAvancadosModalForm}
                      isAbleToBuscar={isAbleToBuscar}
                      tematica={values.tematica}
                      initialValues={values.filtrosAvancados}
                    />
                  </Route>
                </Switch>
              </Cell>
            )}
          </Grid>
        </Cell>
      </Grid>
      <SearchFooter
        onSubmit={handleOnSubmit}
        isAbleToBuscar={isAbleToBuscar}
        isFilterDefault={isFilterSetToDefault}
        onResetToDefault={handleResetFilterToTematicaDefault}
        loading={loading}
      />
    </VFlow>
  )
}

const styles = {
  cell: css`
    margin-top: 1rem;
  `,
}
